import { connect } from 'react-redux'
import { rem } from 'polished'
import React, { ComponentProps, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import {
  MIN_VALID_OFFERS_TO_SHOW_RESULTS,
  MIN_INVALID_OFFERS_TO_SHOW_NO_MATCHES,
  NLS_NO_EXACT_MATCHES_MESSAGE,
  NLS_NO_EXACT_MATCHES_MESSAGE_SHORT
} from 'constants/naturalLanguageSearch'
import { OfferListSortOption } from 'constants/offerListFilters'
import config from 'constants/config'

import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import TextButton from 'LuxKit/Button/TextButton'

import { addGTMEvent } from 'api/googleTagManager'
import { ANYWHERE_PLACE_ID } from '../../../constants/search'
import { DestinationLandingPageType } from '../DestinationLandingPage/DestinationLandingPageUtils'
import { EnabledFilters } from 'components/Search/type'
import { pluralizeToString } from 'lib/string/pluralize'
import { searchSwitchViewClick } from 'analytics/eventDefinitions'
import { selectLoggedIn } from '../../../selectors/accountSelectors'
import { useScreenSizeOnly, useScreenSizeUp } from '../../../hooks/useScreenSize'
import * as Analytics from 'analytics/analytics'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getOfferListKey from 'lib/offer/offerListKey'
import Group from '../../utils/Group'
import HotelBroadSearchLocation from './HotelSearchBroadSearchLocation'
import HotelSearchAddDestinationToggle from './HotelAddDestinationToggle/HotelSearchAddDestinationToggle'
import OfferListFilterSidebar from 'components/OfferList/OfferListFilter/OfferListFilterSidebar'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import VerticalSpacer from '../../Common/Spacing/VerticalSpacer'

const Container = styled(CSSBreakpoint)`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const MobileContainer = styled(CSSBreakpoint)`
  width: 100%;
`

const FilterAndCountContainer = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: ${rem(16)};
  }
`

const ButtonContainer = styled.div`
  display: flex;
  > * + * {
    margin-left: ${rem(8)};
  }
`

interface MappedStateProps {
  availableFilters?: App.OfferListFilterOptions
  isLoggedIn: boolean
  userId?: string
}

type InheritedOfferSortControlProps = Pick<ComponentProps<typeof OfferSortControl>, 'onSortChange'>
type InheritedOfferListFilterSidebarProps = Pick<ComponentProps<typeof OfferListFilterSidebar>, 'onFilterChange'>

interface Props extends InheritedOfferSortControlProps, InheritedOfferListFilterSidebarProps {
  mainListTotal: number
  sortOptions?: Array<OfferListSortOption>
  mapViewUrl?: string
  isFlexible?: boolean
  shouldShowFilters?: boolean
  filters: App.OfferListFilters
  windowSearch: string
  showOfferCount?: boolean
  showHolidayTypes?: boolean
  query?: string
  numberOfInvalidOffers?: number
  numberOfValidOffers?: number
  searchType?: string
  enabledFilters?: EnabledFilters
  fetching?: boolean
  countTextOverride?: string
  specificPlaces?: Array<App.SpecificPlace>
  destinationName?: string
  isDestinationLandingPage?: boolean
  urlLocationForDestinationLandingPage?: string
  pageTypeForDestinationLandingPage?: DestinationLandingPageType
  holidayTypeForDestinationLandingPage?: string
}

function OfferCountAndSort(props: Props & MappedStateProps) {
  const {
    mainListTotal,
    sortOptions,
    mapViewUrl,
    isFlexible,
    shouldShowFilters = false,
    showOfferCount = true,
    query,
    numberOfInvalidOffers = undefined,
    numberOfValidOffers = undefined,
    filters,
    windowSearch,
    searchType,
    enabledFilters,
    fetching,
    countTextOverride,
    specificPlaces,
    destinationName,
    isDestinationLandingPage,
    pageTypeForDestinationLandingPage,
    holidayTypeForDestinationLandingPage,
    isLoggedIn,
    userId,
    onFilterChange,
    onSortChange,
  } = props
  const propertySearch = searchType === 'property'
  const { isFilterPanelOpen, toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const isDesktopUp = useScreenSizeUp('desktop')

  const isMobile = useScreenSizeOnly('mobile')

  const countText = (() => {
    if (countTextOverride) {
      return countTextOverride
    }
    if (propertySearch) {
      return ''
    }
    if (isMobile) {
      return pluralizeToString('offer', mainListTotal)
    }
    return isFlexible ? `Showing ${pluralizeToString('offer', mainListTotal)} with cheapest availabilities for your dates` : `Showing ${pluralizeToString('offer', mainListTotal)}`
  })()

  const isLuxuryEscapes = config.BRAND === 'luxuryescapes'
  const hasSortOptions = sortOptions && sortOptions?.length > 0

  const handleMapClick = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'search_map_view',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })
    addGTMEvent(searchSwitchViewClick())
  }, [])

  const nlsMessage = useMemo(() => {
    if (!query?.length || numberOfValidOffers === undefined || numberOfInvalidOffers === undefined) return
    if (numberOfValidOffers >= MIN_VALID_OFFERS_TO_SHOW_RESULTS) return
    if (numberOfInvalidOffers < Math.min(MIN_INVALID_OFFERS_TO_SHOW_NO_MATCHES, mainListTotal)) return

    if (isDesktopUp) return NLS_NO_EXACT_MATCHES_MESSAGE
    else return NLS_NO_EXACT_MATCHES_MESSAGE_SHORT
  }, [numberOfValidOffers, numberOfInvalidOffers, mainListTotal, query, isDesktopUp])

  const countTextWithNls = useMemo(() => {
    if (countText && nlsMessage) return `${countText} - ${nlsMessage}`
    if (countText) return countText
    if (nlsMessage) return nlsMessage
    return ''
  }, [countText, nlsMessage])

  return (<>
    <div>
      <VerticalSpacer gap={16}>
        {!!specificPlaces?.length && !!destinationName &&
          <HotelBroadSearchLocation
            specificPlaces={specificPlaces}
            destinationName={destinationName}
            isDestinationLandingPage={isDestinationLandingPage}
            pageTypeForDestinationLandingPage={pageTypeForDestinationLandingPage}
            holidayTypeForDestinationLandingPage={holidayTypeForDestinationLandingPage}
          />}
        <MobileContainer max="mobile">
          <Group direction="horizontal" horizontalAlign="space-between">
            {showOfferCount && !fetching && (
              <BodyText variant="medium">{countTextWithNls}</BodyText>
            )}
            {isLuxuryEscapes && isLoggedIn && filters.destinationId && filters.destinationId !== ANYWHERE_PLACE_ID && userId && <HotelSearchAddDestinationToggle userId={userId} placeId={filters.destinationId} />}
          </Group>
        </MobileContainer>
        <Container min="tablet">
          <FilterAndCountContainer>
            {shouldShowFilters && <AssistChip size="medium" variant="filled" startIcon={<LineFilterAltIcon />} onClick={toggleFilterPanel}>
              Filters
            </AssistChip>}
            {showOfferCount && !fetching && <BodyText variant="large">{countTextWithNls}</BodyText>}
          </FilterAndCountContainer>
          <ButtonContainer>
            {hasSortOptions && <OfferSortControl sortOptions={sortOptions} onSortChange={onSortChange} />}
            {mapViewUrl &&
              <TextButton
                kind="secondary"
                variant="dark"
                to={mapViewUrl}
                startIcon={<LineMapIcon />}
                onClick={handleMapClick}
              >
                Map view
              </TextButton>}
          </ButtonContainer>
        </Container>
      </VerticalSpacer>
    </div>
    {!!enabledFilters && <OfferListFilterSidebar
      open={isFilterPanelOpen}
      onClose={toggleFilterPanel}
      filters={filters}
      sortOptions={sortOptions}
      search={windowSearch}
      enabledFilters={enabledFilters}
      onFilterChange={onFilterChange}
    />}
  </>)
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const offerListKey = getOfferListKey(ownProps.filters)

  return {
    availableFilters: appState.offer.offerListFilterOptions[offerListKey],
    isLoggedIn: selectLoggedIn(appState),
    userId: appState.auth.account.memberId,
  }
})(OfferCountAndSort)
